/**
 * Translating between the send-schedule UI and the wire shape.
 *
 * The server keeps two mutually-exclusive columns (`inviteSendAt` OR
 * `inviteSendOffsetDays`, enforced by a DB CHECK) and the form presents
 * three radio choices. Extracted so the create page and the detail page
 * cannot disagree about the mapping — a half-applied conversion is exactly
 * what leaves both fields set and produces a 422 the operator cannot act
 * on.
 *
 * Both fields are always sent together on a PATCH, because the server
 * treats them as a pair: sending either makes both authoritative. That is
 * what lets one save switch a cohort from a fixed date to an offset.
 */
import type { SendMode } from './cohort-form-fields'

export interface SendScheduleState {
  mode: SendMode
  /** `datetime-local` value ('' when unset). */
  fixedAt: string
  /** Kept as a string so an in-progress '-' isn't coerced to 0. */
  offsetDays: string
}

export const EMPTY_SEND_SCHEDULE: SendScheduleState = {
  mode: 'none',
  fixedAt: '',
  offsetDays: '-7',
}

/**
 * What a NEW cohort starts with — relative, the day before the start.
 *
 * CREATE-ONLY: the edit form always reflects the saved row via
 * `sendScheduleFromCohort`, never this. Relative is the safe default
 * because "no schedule" mails each student at CREATION time, and a cohort
 * created weeks ahead of its start then sends invites whose links expire
 * before day one (the 30-day-TTL warning exists for exactly that).
 * `-1` keeps the dead-link window minimal; the trade-off is that a slow
 * email-opener activates after the program has begun — widen to `-7`
 * on the cohort when the audience needs the buffer.
 */
export const CREATE_DEFAULT_SEND_SCHEDULE: SendScheduleState = {
  mode: 'offset',
  fixedAt: '',
  offsetDays: '-1',
}

/** Server shape → form state. */
export function sendScheduleFromCohort(cohort: {
  inviteSendAt: string | null
  inviteSendOffsetDays: number | null
}): SendScheduleState {
  if (cohort.inviteSendOffsetDays !== null) {
    return {
      mode: 'offset',
      fixedAt: '',
      offsetDays: String(cohort.inviteSendOffsetDays),
    }
  }
  if (cohort.inviteSendAt !== null) {
    return {
      mode: 'fixed',
      // `datetime-local` wants 'YYYY-MM-DDTHH:mm' with no zone. Slicing the
      // ISO string keeps the displayed wall-clock time as the server sent
      // it rather than shifting it into the operator's local zone.
      fixedAt: cohort.inviteSendAt.slice(0, 16),
      offsetDays: EMPTY_SEND_SCHEDULE.offsetDays,
    }
  }
  return EMPTY_SEND_SCHEDULE
}

/** Form state → the two wire fields. Exactly one is ever non-null. */
export function sendScheduleToBody(state: SendScheduleState): {
  inviteSendAt: string | null
  inviteSendOffsetDays: number | null
} {
  if (state.mode === 'offset') {
    const parsed = Number(state.offsetDays)
    return {
      inviteSendAt: null,
      // An unparseable in-progress value ('' or '-') becomes "nothing
      // scheduled" rather than 0, which would mean "send on the start
      // date" — a real schedule nobody asked for.
      inviteSendOffsetDays: Number.isFinite(parsed) && state.offsetDays !== '' ? parsed : null,
    }
  }
  if (state.mode === 'fixed' && state.fixedAt !== '') {
    return { inviteSendAt: new Date(state.fixedAt).toISOString(), inviteSendOffsetDays: null }
  }
  return { inviteSendAt: null, inviteSendOffsetDays: null }
}
